import { useEffect, useState } from "react";

import apiService, { type CatalogModule } from "@/services/api";

/**
 * Реестр модулей платформы (GET /v2/modules), общий на всё приложение.
 *
 * Реестр спрашивают сразу трое: боковое меню, каталог проверок и
 * универсальная страница модуля. Запрашивать его в каждом компоненте заново
 * незачем — состав модулей меняется с релизом, а не по ходу сессии, поэтому
 * держим один общий промис на время жизни вкладки.
 */

let pending: Promise<CatalogModule[]> | null = null;
let cached: CatalogModule[] | null = null;

/** Сбрасывает кэш. Нужен тестам, чтобы соседние случаи не видели чужой ответ. */
export function resetModuleCatalog(): void {
  pending = null;
  cached = null;
}

function load(): Promise<CatalogModule[]> {
  if (cached) return Promise.resolve(cached);
  if (!pending) {
    pending = apiService
      .listModules()
      .then((list) => {
        cached = list;
        return list;
      })
      .catch((err) => {
        // Неудачу не кэшируем: следующий заход должен попробовать снова.
        pending = null;
        throw err;
      });
  }
  return pending;
}

export interface ModuleCatalogState {
  modules: CatalogModule[];
  loading: boolean;
  error: string | null;
}

export function useModuleCatalog(): ModuleCatalogState {
  const [state, setState] = useState<ModuleCatalogState>(() => ({
    modules: cached ?? [],
    loading: cached === null,
    error: null,
  }));

  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    load()
      .then((modules) => {
        if (!cancelled) setState({ modules, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Не удалось загрузить каталог модулей";
        setState({ modules: [], loading: false, error: message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export default useModuleCatalog;
